import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

interface MonthData {
  month: number;
  label: string;
  overall: number;
  wealth: number;
  love: number;
  career: number;
  health: number;
  relation: number;
  keyword: string;
  advice: string;
  luckyColor: string;
}

const MONTH_LABELS = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

const MONTH_KEYWORDS = [
  '새 출발', '인내', '도약', '성장', '열정', '절정',
  '전환', '수확', '정리', '결실', '깊이', '완성'
];

const MONTH_ADVICE = [
  '새로운 시작에 두려움보다 설렘을 가지세요. 작은 행동이 큰 변화의 씨앗입니다.',
  '서두르지 마세요. 지금의 노력은 반드시 봄에 꽃을 피웁니다.',
  '움츠렸던 에너지가 폭발할 시기입니다. 기회를 두 손으로 잡으세요.',
  '인간관계에 투자하세요. 지금의 연결이 미래의 귀인이 됩니다.',
  '당신의 열정이 주변을 밝히는 시기입니다. 리더십을 발휘하세요.',
  '올해 가장 에너지가 높은 달입니다. 중요한 결정을 이 달에 하세요.',
  '방향을 재점검하는 시기입니다. 초심으로 돌아가면 답이 보입니다.',
  '노력의 결실이 보이기 시작합니다. 포기하지 마세요.',
  '불필요한 것을 정리하면 새로운 공간이 생깁니다.',
  '가을의 결실처럼 당신의 노력이 구체적 성과로 나타납니다.',
  '깊이 생각하고 신중하게 움직이세요. 내면의 목소리를 들으세요.',
  '한 해를 마무리하며 감사함으로 채우세요. 준비된 자에게 기회가 옵니다.',
];

const LUCKY_COLORS = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#06B6D4', '#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B', '#3B82F6', '#A855F7', '#10B981'];

export function generateMonthData(birthDate: string, year: number): MonthData[] {
  const seed = birthDate.split('').reduce((a, c) => a + c.charCodeAt(0), 0);

  return MONTH_LABELS.map((label, idx) => {
    const m = idx + 1;
    const base = (seed + m * 13 + year) % 100;
    const wealth = ((seed * m + 7) % 4) + 2;
    const love = ((seed + m * 11) % 4) + 2;
    const career = ((seed + m * 17) % 4) + 2;
    const health = ((seed * 3 + m * 5) % 4) + 2;
    const relation = ((seed + m * 23) % 4) + 2;
    const overall = Math.round((wealth + love + career + health + relation) / 5);

    return {
      month: m,
      label,
      overall,
      wealth,
      love,
      career,
      health,
      relation,
      keyword: MONTH_KEYWORDS[idx],
      advice: MONTH_ADVICE[idx],
      luckyColor: LUCKY_COLORS[idx],
    };
  });
}

const SCORE_CONFIG = [
  { score: 5, label: '최길(最吉)', color: 'text-amber-400', bg: 'bg-amber-500/20 border-amber-500/40', bar: 'bg-amber-400' },
  { score: 4, label: '길(吉)', color: 'text-emerald-400', bg: 'bg-emerald-500/20 border-emerald-500/40', bar: 'bg-emerald-400' },
  { score: 3, label: '평(平)', color: 'text-purple-300', bg: 'bg-purple-500/15 border-purple-500/30', bar: 'bg-purple-400' },
  { score: 2, label: '주의', color: 'text-orange-400', bg: 'bg-orange-500/15 border-orange-500/30', bar: 'bg-orange-400' },
  { score: 1, label: '흉(凶)', color: 'text-red-400', bg: 'bg-red-500/15 border-red-500/30', bar: 'bg-red-400' },
];

function scoreConfig(s: number) {
  return SCORE_CONFIG.find((c) => c.score === s) ?? SCORE_CONFIG[2];
}

const CATEGORIES = [
  { key: 'wealth', icon: 'ri-money-dollar-circle-line', label: '재물운', color: 'text-amber-400' },
  { key: 'love', icon: 'ri-heart-3-line', label: '애정운', color: 'text-rose-400' },
  { key: 'career', icon: 'ri-briefcase-4-line', label: '직업운', color: 'text-sky-400' },
  { key: 'health', icon: 'ri-heart-pulse-line', label: '건강운', color: 'text-emerald-400' },
  { key: 'relation', icon: 'ri-group-line', label: '대인운', color: 'text-violet-400' },
] as const;

interface Props {
  birthDate: string;
  birthTime: string;
  name?: string;
  dayStem?: string;
  year: number;
  onMonthSelect?: (month: MonthData | null) => void;
  selectedMonth: MonthData | null;
}

export default function AnnualFortuneGrid({ birthDate, birthTime, name, dayStem, year, onMonthSelect, selectedMonth }: Props) {
  const months = useMemo(() => generateMonthData(birthDate, year), [birthDate, year]);

  const bestMonth = [...months].sort((a, b) => b.overall - a.overall)[0];
  const worstMonth = [...months].sort((a, b) => a.overall - b.overall)[0];

  // Category averages
  const catAvg = CATEGORIES.map((cat) => {
    const avg = months.reduce((s, m) => s + m[cat.key as keyof MonthData] as number, 0) / 12;
    return { ...cat, avg: Math.round(avg * 10) / 10 };
  });

  return (
    <div className="space-y-8">
      {/* Year overview cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {catAvg.slice(0, 4).map((cat) => {
          const cfg = scoreConfig(Math.round(cat.avg));
          return (
            <div key={cat.key} className={`p-4 rounded-2xl border ${cfg.bg} text-center`}>
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                <i className={`${cat.icon} ${cat.color} text-xl`}></i>
              </div>
              <p className={`font-black text-2xl ${cfg.color}`}>{cat.avg.toFixed(1)}</p>
              <p className="text-purple-300/70 text-xs mt-0.5">{cat.label}</p>
              <p className={`text-xs font-bold mt-1 ${cfg.color}`}>{cfg.label}</p>
            </div>
          );
        })}
      </div>

      {/* Best / Worst months */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-sun-fill text-amber-400 text-sm"></i>
            </div>
            <span className="text-amber-400 text-xs font-bold">최고 운세의 달</span>
          </div>
          <p className="text-white font-black text-xl">{bestMonth.label}</p>
          <p className="text-amber-300/70 text-xs mt-1">{bestMonth.keyword} · 전체 {bestMonth.overall}점</p>
        </div>
        <div className="p-4 rounded-2xl bg-slate-500/10 border border-slate-500/30">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-shield-check-line text-slate-300 text-sm"></i>
            </div>
            <span className="text-slate-300 text-xs font-bold">주의가 필요한 달</span>
          </div>
          <p className="text-white font-black text-xl">{worstMonth.label}</p>
          <p className="text-slate-300/70 text-xs mt-1">{worstMonth.keyword} · 전체 {worstMonth.overall}점</p>
        </div>
      </div>

      {/* 12 Months Grid */}
      <div>
        <p className="text-purple-300/60 text-xs font-semibold uppercase tracking-wide mb-4">월별 운세 (클릭하면 상세 보기)</p>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {months.map((m) => {
            const cfg = scoreConfig(m.overall);
            const isSelected = selectedMonth?.month === m.month;
            const isNow = new Date().getMonth() + 1 === m.month;
            return (
              <button
                key={m.month}
                onClick={() => onMonthSelect?.(isSelected ? null : m)}
                className={`relative p-3 rounded-2xl border transition-all duration-200 cursor-pointer text-center ${
                  isSelected
                    ? 'border-primary-gold bg-primary-gold/20 scale-105'
                    : `${cfg.bg} hover:scale-105 hover:border-primary-gold/40`
                }`}
              >
                {isNow && (
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2">
                    <span className="text-[9px] bg-primary-gold text-black font-black px-1.5 py-0.5 rounded-full whitespace-nowrap">NOW</span>
                  </div>
                )}
                <p className="text-white font-bold text-sm">{m.label}</p>
                <p className={`font-black text-xl ${cfg.color}`}>{m.overall}</p>
                <p className={`text-[10px] font-semibold ${cfg.color}`}>{cfg.label}</p>
                <p className="text-purple-400/60 text-[9px] mt-1 truncate">{m.keyword}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Month Detail Panel */}
      {selectedMonth && (
        <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark/90 to-mystic-dark/90 border border-primary-gold/30 animate-fadeIn">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-white font-black text-xl">{selectedMonth.label} 상세 운세</h3>
              <p className="text-primary-gold text-sm mt-0.5">{selectedMonth.keyword} — {scoreConfig(selectedMonth.overall).label}</p>
            </div>
            <button onClick={() => onMonthSelect?.(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-purple-400 transition-all cursor-pointer">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>

          {/* Category breakdown bars */}
          <div className="space-y-3 mb-5">
            {CATEGORIES.map((cat) => {
              const val = selectedMonth[cat.key as keyof MonthData] as number;
              const cfg = scoreConfig(val);
              return (
                <div key={cat.key} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} ${cat.color} text-sm`}></i>
                  </div>
                  <span className="text-purple-200 text-xs w-14 flex-shrink-0">{cat.label}</span>
                  <div className="flex-1 h-2.5 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${cfg.bar} transition-all duration-700`} style={{ width: `${(val / 5) * 100}%` }}></div>
                  </div>
                  <span className={`text-xs font-bold w-8 text-right ${cfg.color}`}>{val}/5</span>
                </div>
              );
            })}
          </div>

          {/* Advice */}
          <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border border-indigo-500/20">
            <p className="text-indigo-300 text-xs font-semibold mb-1.5">이달의 운세 조언</p>
            <p className="text-purple-200/80 text-sm leading-relaxed">{selectedMonth.advice}</p>
          </div>

          {/* Lucky color dot */}
          <div className="mt-3 flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-white/20" style={{ backgroundColor: selectedMonth.luckyColor }}></div>
            <span className="text-purple-400/60 text-xs">이달의 행운 컬러</span>
          </div>
        </div>
      )}
    </div>
  );
}
